<script>
import { nomeMes, plural } from "@/utils/minhas-metas";

const ICONES_SELO = {
  batida: "bx bx-check-shield",
  gordura: "bx bx-shield-plus",
  pessoal: "bx bx-target-lock",
  superou: "bx bx-rocket",
};

/**
 * Selos + últimos fechamentos do "Minhas Metas" (bloco `gamificacao` do GET metas/painel).
 * Compartilhado entre o painel (minhas-metas/index.vue) e a Jornada diária.
 *
 * `revelar` (opcional): quando false os itens aparecem sem animação; quando true entram em
 * cascata (a Jornada diária usa isso). Sempre respeita prefers-reduced-motion.
 */
export default {
  name: "MetasSelosHistorico",
  props: {
    gamificacao: { type: Object, required: true },
    legenda: { type: String, default: "" },
    mostrarSelos: { type: Boolean, default: true },
    revelar: { type: Boolean, default: false },
  },
  methods: {
    nomeMes,
    plural,
    iconeSelo(key) {
      return ICONES_SELO[key] || "bx bx-medal";
    },
    atraso(i) {
      return this.revelar ? { animationDelay: i * 90 + "ms" } : null;
    },
  },
};
</script>

<template>
  <div class="msh" :class="{ 'msh-revelar': revelar }">
    <ul v-if="mostrarSelos" class="msh-selos" aria-label="Selos">
      <li
        v-for="(s, i) in gamificacao.selos"
        :key="s.key"
        class="msh-selo"
        :class="{ ok: s.conquistado }"
        :style="atraso(i)"
        :title="s.conquistado ? 'Conquistado' : 'Chegue a ' + s.meta"
      >
        <i :class="iconeSelo(s.key)" aria-hidden="true"></i>
        <span>{{ s.label }}</span>
        <span class="sr-only">{{ s.conquistado ? "(conquistado)" : "(ainda não)" }}</span>
      </li>
      <li class="msh-selo msh-selo-seq" :class="{ ok: gamificacao.sequencia > 0 }" :style="atraso(gamificacao.selos.length)">
        <i class="bx bxs-flame" aria-hidden="true"></i>
        <span>{{ gamificacao.sequencia }} {{ plural(gamificacao.sequencia, "mês seguido", "meses seguidos") }} batendo</span>
      </li>
    </ul>

    <ol class="msh-hist" aria-label="Últimos fechamentos">
      <li
        v-for="(h, i) in gamificacao.historico"
        :key="h.ano + '-' + h.mes"
        :class="{ ok: h.batida, sem: !h.dados }"
        :style="atraso(i)"
        :title="h.dados ? h.total + ' metas' : 'Sem dados processados'"
      >
        <span class="msh-hist-mes">{{ nomeMes(h.mes).slice(0, 3) }}</span>
        <span class="msh-hist-val">{{ h.dados ? h.total : "–" }}</span>
        <span class="sr-only">{{ h.dados ? (h.batida ? "meta batida" : "meta não batida") : "sem dados" }}</span>
      </li>
    </ol>
    <p v-if="legenda" class="msh-legenda">{{ legenda }}</p>
  </div>
</template>

<style scoped>
.msh-selos {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0 0;
  padding: 0;
  list-style: none;
}
.msh-selo {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3125rem 0.75rem;
  border: 1px dashed var(--st-border);
  border-radius: 999px;
  font-size: 0.8125rem;
  color: var(--st-text-muted);
}
.msh-selo i {
  font-size: 1rem;
}
.msh-selo.ok {
  border-style: solid;
  border-color: var(--st-success);
  background: var(--st-success-soft);
  color: var(--st-success-strong);
}
.msh-selo-seq.ok {
  border-color: var(--st-warning);
  background: var(--st-warning-soft);
  color: var(--st-warning-strong);
}
.msh-hist {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.375rem;
  margin: 1rem 0 0.25rem;
  padding: 0;
  list-style: none;
}
.msh-hist li {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.375rem 0;
  border-radius: var(--st-radius);
  background: var(--st-canvas);
  font-size: 0.75rem;
  color: var(--st-text-muted);
}
.msh-hist li.ok {
  background: var(--st-success-soft);
  color: var(--st-success-strong);
}
.msh-hist li.sem {
  background: var(--st-surface);
  border: 1px dashed var(--st-border);
}
.msh-hist-mes {
  text-transform: uppercase;
  font-weight: 600;
}
.msh-hist-val {
  font-size: 1rem;
  font-weight: 700;
}
.msh-legenda {
  margin: 0;
  font-size: 0.75rem;
  color: var(--st-text-muted);
}
.msh-revelar .msh-selo,
.msh-revelar .msh-hist li {
  animation: msh-entrar 0.4s ease-out both;
}
@keyframes msh-entrar {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@media (max-width: 575.98px) {
  .msh-hist {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (prefers-reduced-motion: reduce) {
  .msh-revelar .msh-selo,
  .msh-revelar .msh-hist li {
    animation: none;
  }
}
</style>
